<template>
  <div>
    <SelectorPanel
      type="stability"
      :text="title"
      :dialog-full-height="true"
      :dialog-width="270"
      :disabled="disabled"
      :force-close="forceClose"
      @resized="handleResize"
      content-class="hyp-dialog-risk-card-text"
      content-wrapper-class="hyp-dialog-risk-card"
    >
      <template v-slot:append>
        <PulseAndRiskDefinitions
          :initial="'BOTH'"
          :new-icon="false"
        ></PulseAndRiskDefinitions>
      </template>
      <template>
        <v-container
          class="hyp-pulse-risk-container"
          :class="{
            'hyp-pulse-risk-container-min': isMinimized,
            'hyp-pulse-risk-container-full': !isMinimized,
          }"
        >
          <div class="pull-left hyp-pulse-risk-filters">
            <SelectorPanelHeader
              type="stability"
              :title="title"
              @click-close="handleClickClose"
              class="px-0"
            ></SelectorPanelHeader>

            <div class="hyp-main-container">
              <div class="toggle-container">
                <v-btn-toggle
                  mandatory
                  color="primary"
                  v-model="typeSelected"
                  class="mb-5"
                >
                  <v-btn
                    :disabled="hasEssentials"
                    value="pulse"
                    width="125"
                    height="36"
                  >
                    {{ labels.pulse }}
                  </v-btn>
                  <v-btn value="risks" width="125" height="36">
                    {{ labels.cr_risk_ratings }}
                  </v-btn>
                </v-btn-toggle>

                <div>
                  <StabilitySelector
                    v-show="pulseSelected"
                    :enabled="pulseSelected"
                  ></StabilitySelector>
                  <RisksSelector
                    v-show="risksSelected"
                    :enabled="this.typeSelected === 'risks'"
                    @selected="handleSelectedRisks"
                  ></RisksSelector>
                </div>
              </div>
            </div>
          </div>
          <div
            class="pull-left hyp-content-container"
            :style="style"
            v-resize="this.handleHypRiskContentResize"
          >
            <RisksContent
              v-if="risksSelected && !isMinimized"
              :selected-location="selectedLocation"
              :is-minimized="isMinimized"
              @click-minimize="handleClickMinimize"
              @click-close="handleClickClose"
            ></RisksContent>
          </div>
          <div class="clearfix"></div>
        </v-container>
      </template>
    </SelectorPanel>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import { bbox } from "@turf/turf";

import { getMapLayers } from "@/services/controlRisks/risks.service.js";
import { getAllCountries } from "@/common/maps/utilities";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_HOME,
} from "@/store";
import { mapGetters } from "vuex";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { LABELS } from "@/common/constants/labels";
import { LeftSidePanelWidthNumber } from "@/common/constants/layout";
import { hasEssentialsAccess } from "@/services/user/user.service";

export default {
  name: "PulseAndRiskSelector",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SelectorPanel: () => import("@/components/common/SelectorPanel"),
    SelectorPanelHeader: () =>
      import("@/components/common/SelectorPanelHeader"),
    StabilitySelector: () =>
      import("@/components/grids/stability/StabilitySelector"),
    RisksSelector: () =>
      import("@/components/controlRisks/risks/RisksSelector"),
    RisksContent: () => import("@/components/controlRisks/risks/RisksContent"),
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
  },
  data() {
    return {
      forceClose: null,
      typeSelected: "pulse",
      isMinimized: true,
      selectedLocation: null,
      countries: null,
      labels: LABELS,
      sidebarWidth: 887,
      hasEssentials: hasEssentialsAccess(),
      windowSize: {
        x: window.innerWidth,
        y: window.innerHeight,
      },
    };
  },
  async mounted() {
    if (this.hasEssentials) {
      this.typeSelected = "risks";
    } else {
      if (lodashGet(this.preferences, "controlRisks.riskRatings.mapEnabled")) {
        this.typeSelected = "risks";
      } else {
        this.typeSelected = "pulse";
      }
    }
    await getMapLayers();
    this.countries = await getAllCountries();
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    title() {
      return `${this.labels.cr_risk} & ${this.labels.stability}`;
    },
    pulseSelected() {
      return this.typeSelected === "pulse";
    },
    risksSelected() {
      return this.typeSelected === "risks";
    },
    selectedData() {
      return this.selectorPanel.selectedData;
    },
    style() {
      // 300 - selector panel , 24 - total container padding
      let dialogWidth = this.windowSize.x - LeftSidePanelWidthNumber - 300 - 24;

      return {
        width: `${dialogWidth}px !important`,
      };
    },
  },
  methods: {
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    handleClickMinimize() {
      this.isMinimized = !this.isMinimized;
      if (this.isMinimized) {
        this.selectedLocation = null;
      }
    },
    handleSelectedRisks(location) {
      if (
        this.selectorPanel?.selected &&
        this.selectorPanel.selected === "stability"
      ) {
        this.selectorPanel.selectedData = null;
      }
      if (location) {
        let fitBbox;
        const len = this.countries?.length || 0;
        for (let i = 0; i < len; i++) {
          const country = this.countries[i];
          if (
            lodashGet(country, "properties.iso2") &&
            country.properties.iso2 === location.iso2Code &&
            lodashGet(country, "geometry.coordinates.length")
          ) {
            if (country?.properties?.viewBounds) {
              fitBbox = [
                [
                  country.properties.viewBounds[0],
                  country.properties.viewBounds[1],
                ],
                [
                  country.properties.viewBounds[2],
                  country.properties.viewBounds[3],
                ],
              ];
            } else if (lodashGet(country, "geometry.coordinates.length")) {
              const boundingBox = bbox(country);
              if (
                boundingBox &&
                boundingBox.length &&
                boundingBox.length === 4
              ) {
                fitBbox = [
                  [boundingBox[0], boundingBox[1]],
                  [boundingBox[2], boundingBox[3]],
                ];
              }
            }
          }
        }
        if (this.map && fitBbox) {
          const fitBoundsPadding = 40;
          // Since new country panel will be full screen, non map view, removed timeout to make sure it does fly before panel opens to keep it ready when country panel is minimized
          this.map.fitBounds(fitBbox, {
            padding: {
              top: fitBoundsPadding,
              bottom: fitBoundsPadding,
              right: fitBoundsPadding + LeftSidePanelWidthNumber,
              left: fitBoundsPadding + 300,
            },
          });
        }
      }
      this.selectedLocation = location;
      this.isMinimized = !!!location;
    },
    handleResize(params) {
      this.sidebarWidth = params;
    },
    handleHypRiskContentResize(params) {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    handleSelectedData() {
      // set risks when risk rating country is selected
      if (
        !this.selectedData ||
        !this.selectedData.code ||
        (!this.countries
          .map((c) => c.properties.iso2)
          .includes(this.selectedData) &&
          !this.countries
            .map((c) => c.properties.iso3)
            .includes(this.selectedData))
      ) {
        return;
      }
      this.typeSelected = "risks";
    },
  },
  watch: {
    typeSelected() {
      if (this.typeSelected !== "risks") {
        this.isMinimized = true;
      }
    },
    selectedData() {
      this.handleSelectedData();
    },
  },
};
</script>

<style scoped>
.hyp-pulse-risk-container {
  padding: 0 !important;
  margin: 0 !important;
  max-height: unset;
  height: 100%;
  max-width: unset;
  background-color: #edeced;
}
.hyp-pulse-risk-container-min {
  width: 270px;
}
.toggle-container {
  display: grid;
  place-items: center;
}
.hyp-pulse-risk-filters {
  padding: 0 24px 0 0 !important;
  height: 100%;
  width: 300px;
  background-color: #f7f7fa;
}
</style>

<style>
.v-card__text.hyp-dialog-risk-card-text {
  padding-right: 0 !important;
  height: 100% !important;
  padding-bottom: 0 !important;
}
.hyp-dialog-content-container.hyp-dialog-risk-card {
  padding: 0 !important;
}
</style>
